// Pure cooking-state logic, ported from the mobile app
// (cooking_logic.dart + temperature.dart). No network or framework deps.
// This is the source of truth for how a stove's state is derived and must stay
// in lock-step with the app so the two never disagree.

// Reduced power-cutoff (PCO) relay state.
// ON/OFF/LOCK ⇒ a PCO is attached. ONLINE ⇒ reachable but no PCO. UNKNOWN ⇒
// parse failure (do not cache).
export type PcoState = "ON" | "OFF" | "LOCK" | "ONLINE" | "UNKNOWN";

export type DisplayState =
  | "offline"
  | "blocked"
  | "off"
  | "lock"
  | "cooking"
  | "ovenOn"
  | "cooling"
  | "on"
  | "online"
  | "unknown";

export type TempUnit = "C" | "F";

// Threshold (°C) separating an actively/recently hot burner ("cooling") from
// ambient.
const HOT_BURNER_C = 50;

// Reduce a PCOstatechange record (data[0]) to a single PcoState. Mirrors the
// app's checkShutdown reduction. A missing row / disconnected unit ⇒ ONLINE
// (no PCO hardware); relays disagreeing mid-transition ⇒ ON (fail toward
// "power might be live" for a safety product).
export function reducePcoState(row: any): PcoState {
  if (!row || typeof row !== "object") return "ONLINE";
  const connected = Number(row.currentUnitConnectedStatus);
  if (!connected) return "ONLINE";
  const r1 = Number(row.currentRelay1Status);
  const r2 = Number(row.currentRelay2Status);
  if (r1 === 1 && r2 === 1) return "ON";
  if (r1 === 0 && r2 === 0) {
    // currentIsStoveRemoteUnlocked falsy ⇒ locked ⇒ LOCK; truthy ⇒ OFF.
    return row.currentIsStoveRemoteUnlocked ? "OFF" : "LOCK";
  }
  return "ON";
}

function decodeMaybe(value: unknown): any {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

// Walk a device-status reply tolerantly to find the object holding the device
// state (an `alarms` object, or inline heating/cooking keys). Replies can be
// JSON strings and may be nested under payload/state/data/device_state.
export function extractDeviceState(event: unknown): any | null {
  const v = decodeMaybe(event);
  if (!v || typeof v !== "object") return null;
  if (Array.isArray(v)) {
    for (const item of v) {
      const hit = extractDeviceState(item);
      if (hit) return hit;
    }
    return null;
  }
  const obj = v as Record<string, unknown>;
  if (obj.alarms && typeof obj.alarms === "object") return obj;
  if (
    "any_burner_actively_heating" in obj ||
    "cooking" in obj ||
    "stove_powered_on" in obj
  ) {
    return { alarms: obj, burners: obj.burners };
  }
  for (const key of ["payload", "state", "data", "device_state", "stove_settings"]) {
    if (obj[key] != null) {
      const hit = extractDeviceState(decodeMaybe(obj[key]));
      if (hit) return hit;
    }
  }
  return null;
}

// The alarms map from a device-status reply event, or null.
export function extractAlarms(event: unknown): any | null {
  const ds = extractDeviceState(event);
  if (!ds) return null;
  return ds.alarms && typeof ds.alarms === "object" ? ds.alarms : null;
}

// Max burner temperature (°C) across the reply's burners[] (each {…, t}); null
// when absent.
export function hottestBurnerTempC(deviceState: any): number | null {
  if (!deviceState || typeof deviceState !== "object") return null;
  const burners = deviceState.burners ?? deviceState.alarms?.burners;
  if (!Array.isArray(burners) || burners.length === 0) return null;
  let max = -Infinity;
  for (const b of burners) {
    const t = b && typeof b === "object" ? Number((b as any).t) : NaN;
    if (!Number.isNaN(t) && t > max) max = t;
  }
  return max === -Infinity ? null : max;
}

export function stovePoweredOnFromAlarms(alarms: any): boolean | null {
  if (!alarms || typeof alarms !== "object") return null;
  const v = alarms["stove_powered_on"];
  return typeof v === "boolean" ? v : null;
}

// Whether the live device-status alarms map reports the sensor's view blocked
// (firmware `camera_obstructed`). Absent/non-boolean ⇒ false (treat unknown as
// "not blocked" so a missing field never shows a false alarm).
export function blockedFromAlarms(alarms: any): boolean {
  if (!alarms || typeof alarms !== "object") return false;
  return alarms["camera_obstructed"] === true;
}

// Tri-state cooking derivation. Returns true / false / null (unknown). The
// null must be preserved, it drives hedged copy downstream.
export function cookingFromAlarms(
  alarms: any,
  pcoState: PcoState
): boolean | null {
  if (!alarms || typeof alarms !== "object") return null;
  const heating = alarms["any_burner_actively_heating"];
  if (typeof heating === "boolean") {
    if (!heating) return false;
    const pcoAvailable =
      pcoState === "ON" || pcoState === "OFF" || pcoState === "LOCK";
    // No PCO ⇒ can't use current draw; active heating alone counts.
    if (!pcoAvailable) return true;
    // PCO present ⇒ also require current draw.
    return alarms["stove_powered_on"] === true;
  }
  // Old firmware fallback.
  const legacy = alarms["cooking"];
  return typeof legacy === "boolean" ? legacy : null;
}

// The display-state precedence. Order matters, first match wins:
// offline > blocked > OFF > LOCK > cooking > ovenOn > cooling > ON > ONLINE >
// unknown. Blocked ranks just under offline: a covered/moved sensor is blind to
// the cooktop, so that fact outranks whatever cooking state it last reported.
export function resolveDisplayState(
  isOnline: boolean,
  state: PcoState,
  isCooking: boolean | null,
  hottestTempC: number | null,
  stovePoweredOn: boolean | null,
  blocked: boolean = false
): DisplayState {
  if (!isOnline) return "offline";
  if (blocked) return "blocked";
  if (state === "OFF") return "off";
  if (state === "LOCK") return "lock";
  if (isCooking === true) return "cooking";
  // OFF/LOCK already returned above, so a PCO being attached here means ON.
  const pcoAttached = state === "ON";
  if (
    stovePoweredOn === true &&
    pcoAttached &&
    (hottestTempC == null || hottestTempC <= HOT_BURNER_C)
  ) {
    return "ovenOn";
  }
  if (hottestTempC != null && hottestTempC > HOT_BURNER_C) return "cooling";
  switch (state) {
    case "ON":
      return "on";
    case "ONLINE":
      return "online";
    default:
      return "unknown";
  }
}

// Map a burner temp (°C) to the exact RGB the device shows on its LED:
// a blue→cyan→green→yellow→red gradient. Clamp to [50, 300].
export function firmwareLedColor(tempC: number): {
  r: number;
  g: number;
  b: number;
} {
  const t = Math.max(50, Math.min(300, tempC));
  const v = ((100 - 83) / (300 - 50)) * (t - 50) + 83;
  const u = v / 100;
  let r: number;
  let g: number;
  let b: number;
  if (u < 0.25) {
    r = 0;
    g = 4 * u;
    b = 1;
  } else if (u < 0.5) {
    r = 0;
    g = 1;
    b = 1 + 4 * (0.25 - u);
  } else if (u < 0.75) {
    r = 4 * (u - 0.5);
    g = 1;
    b = 0;
  } else {
    r = 1;
    g = 1 + 4 * (0.75 - u);
    b = 0;
  }
  const ch = (x: number) => Math.max(0, Math.min(255, Math.round(x * 255)));
  return { r: ch(r), g: ch(g), b: ch(b) };
}

export function firmwareLedColorHex(tempC: number): string {
  const { r, g, b } = firmwareLedColor(tempC);
  return `rgb(${r}, ${g}, ${b})`;
}

// Whole-degree temperature in the chosen unit (°F = °C·9/5 + 32).
export function formatTemp(tempC: number, unit: TempUnit): string {
  const v = unit === "F" ? (tempC * 9) / 5 + 32 : tempC;
  return `${Math.round(v)}°${unit}`;
}

// Ambient (room) temperature bands for the dashboard. Building managers
// care about freezing pipes (cold) and dangerous heat (hot); anything in
// between is fine. When per-sensor thresholds ship, swap the constants for
// values pulled from the sensor's user-config.
export const AMBIENT_COLD_C = 5;   // ~41 °F — freezing-pipe risk
export const AMBIENT_HOT_C = 35;   // ~95 °F — dangerous heat

export type AmbientTone = "cold" | "hot" | null;

export function ambientTone(tempC: number | null | undefined): AmbientTone {
  if (tempC == null || Number.isNaN(tempC)) return null;
  if (tempC < AMBIENT_COLD_C) return "cold";
  if (tempC > AMBIENT_HOT_C) return "hot";
  return null;
}
